import { useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import "../../../Preview/index.css";
import useLanguage from "../../../../utils/useLanguage";
import useUpdateDomTitle from "../../../../utils/updateDomTitle";
import { useAdjustBackdropHeight } from "../../../backdrop";
import WelcomeScreen from "../../../common/WelcomeScreen";
import useScroller from "../../../../utils/Scroller";
import { isLight } from "../../../../utils/Color";
import ShareOrCopy from "../../../../utils/share";
import AOS from "aos";
import "aos/dist/aos.css";
import { atcb_action } from "add-to-calendar-button";

import { cn } from "./_internal/cn";
import {
  getEventDisplay,
  isMultiDateCalendarConfig,
} from "./_internal/event-display";
import {
  downloadIcsFile,
  installIosAppleCalendarIntercept,
  isIosBrowser,
} from "./_internal/ios-calendar-fallback";
import EventBackdrop from "./_internal/event-backdrop";
import EventHeader from "./_internal/event-header";
import EventHeroCard from "./_internal/event-hero-card";
import EventCalendarButton from "./_internal/event-calendar-button";
import EventDateCard from "./_internal/event-date-card";
import EventDetailsCard from "./_internal/event-details-card";
import EventAddressCard from "./_internal/event-address-card";
import EventContactCard from "./_internal/event-contact-card";
import EventFacilitiesCard from "./_internal/event-facilities-card";
import EventSocialNetworks from "./_internal/event-social-networks";

const Event = ({ qrData }) => {
  const { t, locale } = useLanguage();
  const { uid } = useParams();

  const display = getEventDisplay(qrData, {
    /* Use the resolved UI language (BCP-47) so event dates format for the
     * selected language rather than falling back to the browser default. */
    locale: locale || undefined,
    viewMoreLabel: t("event.view_more_btn"),
  });

  const backdropElement = useRef(null);
  const backdropEndElement = useRef(null);
  const backdropEndFallbackElement = useRef(null);

  const [welcomeScreen, showWelcomeScreen] = useState(() =>
    Boolean(display.welcomeImage),
  );

  /*
   * The builder phone mockup already draws a fake status bar, so the iframe
   * (event_preview / live postMessage) uses the same mt-10 as the local
   * placeholder. Clicks stay live: filled dates open Add to Calendar, and
   * filled links / map / social keep their hrefs.
   */
  const isBuilderIframe =
    qrData?.event_preview === true || qrData?.live === true;

  const ignoredColors = ["#07B8B8", "#C178C8"];
  const primaryColor = display.primaryColor || "#00296B";
  /* Secondary color drives the "View More" links; fall back to the primary
   * color when the payload omits it so older/no-secondary payloads keep the
   * previous single-color look. */
  const secondaryColor = display.secondaryColor || primaryColor;
  const light = isLight(primaryColor, ignoredColors);
  const titleFont = display.titleFont;
  const textFont = display.textFont;

  useUpdateDomTitle(display.title, primaryColor);

  useEffect(() => {
    AOS.init({ duration: 2000 });
  }, []);

  useEffect(() => {
    const src = display.welcomeImage;
    if (!src) {
      showWelcomeScreen(false);
      return undefined;
    }
    showWelcomeScreen(true);
    const timeoutId = setTimeout(() => {
      /*
       * Same as the other QR types: clear the image after it plays so the
       * next Preview message (same URL) is a change and plays again.
       */
      if (qrData) {
        qrData.screen = false;
        qrData.welcome_screen = false;
      }
      showWelcomeScreen(false);
    }, 3000);
    return () => clearTimeout(timeoutId);
  }, [display.welcomeImage]);

  useAdjustBackdropHeight(
    backdropElement,
    backdropEndElement,
    backdropEndFallbackElement,
    qrData,
  );

  useScroller(qrData);

  const isMultiDateCalendar = isMultiDateCalendarConfig(display.calendarConfig);

  /*
   * Business Requirement:
   * OQG3-2573: add-to-calendar-button 2.6.x rewrote "&" in Outlook subjects
   * as the HTML entity "&#xFF06;", which Outlook then showed literally.
   * 2.9.1+ URL-encodes the subject instead, so the original characters
   * (ampersands, quotes, Unicode, emoji) survive into Outlook/Google/iCal.
   */
  const handleCalendarClick = (event) => {
    /*
     * Business Requirement:
     * OQG3-2559: on iOS the library either shows an extra "Add all" modal
     * (Safari) or an "Open Safari" note (Chrome/Firefox/Edge) instead of
     * handing over the multi-date .ics. Deliver the file straight from the
     * tap: Safari opens it in Calendar, other browsers save it to Files.
     * In-app browsers and other platforms keep the library.
     */
    if (isMultiDateCalendar && isIosBrowser()) {
      downloadIcsFile(display.calendarConfig);
      return;
    }
    atcb_action(display.calendarConfig, event.currentTarget);
  };

  /* The single-date picker is still the library's, but its "Apple" entry has
   * the same iOS limitation, so on those browsers the entry downloads the
   * .ics too. The ref keeps the intercept on the latest config without
   * re-registering the listener on every render. */
  const calendarConfigRef = useRef(display.calendarConfig);
  calendarConfigRef.current = display.calendarConfig;

  useEffect(
    () => installIosAppleCalendarIntercept(() => calendarConfigRef.current),
    [],
  );

  /* The "Add to Calendar" CTA shows whenever the event has valid dates. By
   * default it lives in the hero card (next to the date block); when the user
   * enabled "move to bottom" it renders as the last element on the page. */
  const showCalendarInHero =
    display.showCalendarButton && !display.moveCalendarToBottom;
  const showCalendarAtBottom =
    display.showCalendarButton && display.moveCalendarToBottom;

  return welcomeScreen ? (
    <WelcomeScreen screen={display.welcomeImage} />
  ) : (
    <div className="relative flex flex-col items-center">
      <div
        className="absolute z-10 bg-[#00000042] w-full h-full top-0 hidden"
        id="backdrop"
      ></div>

      <EventBackdrop ref={backdropElement} color={primaryColor} />

      {/*
       * The builder phone mockup already draws a fake status bar, so the iframe
       * uses the same mt-10 as the local placeholder. The published page keeps
       * the larger offset so the share button clears a real phone status bar.
       */}
      <div
        className={cn(
          "flex w-full max-w-[700px] flex-col items-center justify-center px-3 pb-7",
          isBuilderIframe ? "mt-10" : "mt-[45px] xs:mt-[54px]",
        )}
      >
        <div className="flex w-full flex-col gap-5">
          <div className="relative">
            <EventHeader
              title={display.title}
              summary={display.summary}
              titleFont={titleFont}
              textFont={textFont}
              light={light}
              shareLabel={t("business.share")}
              onShare={() => {
                /*
                 * ShareOrCopy only runs when the payload has uId, which the
                 * other QR types store on the document. A scanned Event
                 * payload does not, so use the route uid. Builder preview
                 * stays off because that payload is live.
                 */
                const uId = qrData?.uId || uid;
                ShareOrCopy({
                  ...qrData,
                  title: display.title,
                  ...(uId ? { uId } : {}),
                });
              }}
            />
            {/* Backdrop fallback anchor: when no hero card exists (no link /
             * image / calendar button), the swoosh must still extend past the
             * header so the white title/summary stay on the dark background.
             * Pinned just below the header so the curve clears both lines. */}
            <div
              ref={backdropEndFallbackElement}
              aria-hidden="true"
              className="pointer-events-none absolute bottom-[-40px] left-0 h-0 w-0"
            />
          </div>

          <EventHeroCard
            ref={backdropEndElement}
            image={display.image}
            imageAlt={display.title || t("event.about_title")}
            links={display.links}
            showCalendarButton={showCalendarInHero}
            primaryColor={primaryColor}
            secondaryColor={secondaryColor}
            calendarLabel={t("event.add_to_calander_btn")}
            onCalendarClick={handleCalendarClick}
            textFont={textFont}
          />

          <EventDateCard
            dates={display.dates}
            startLabel={t("event.start")}
            endLabel={t("event.end")}
            dateLabel={t("event.date")}
            timeZone={display.timeZone || null}
            titleFont={titleFont}
            textFont={textFont}
          />

          <EventDetailsCard
            about={display.about}
            title={t("event.about_title")}
            titleFont={titleFont}
            textFont={textFont}
          />

          <EventAddressCard
            address={display.address}
            title={t("event.address_title")}
            showMapLabel={t("event.address_show_map_btn")}
            onShowMap={display.onShowMap}
            titleFont={titleFont}
            textFont={textFont}
          />

          <EventContactCard
            contactName={display.contactName}
            websites={display.websites}
            phones={display.phones}
            emails={display.emails}
            title={t("menu.contact")}
            nameLabel={t("business.name")}
            websiteLabel={t("vcard.website")}
            phoneLabel={t("vcard.phone")}
            emailLabel={t("vcard.email")}
            titleFont={titleFont}
            textFont={textFont}
          />

          <EventFacilitiesCard
            facilities={display.facilities}
            title={t("event.facilities")}
            titleFont={titleFont}
          />

          <EventSocialNetworks
            title={t("business.social_network")}
            blocks={display.socialBlocks}
            titleFont={titleFont}
            textFont={textFont}
          />

          {showCalendarAtBottom && (
            <EventCalendarButton
              primaryColor={primaryColor}
              label={t("event.add_to_calander_btn")}
              onClick={handleCalendarClick}
              textFont={textFont}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Event;
